import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { TeamType } from "@redotech/account-common/team-type";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import BarChart01Icon from "@redotech/redo-icons/bar-chart-01.svg";
import BarChart01FilledIcon from "@redotech/redo-icons/bar-chart-01_filled.svg";
import BoxIcon from "@redotech/redo-icons/box-01.svg";
import BoxFilledIcon from "@redotech/redo-icons/box_filled.svg";
import CallBellIcon from "@redotech/redo-icons/call-bell.svg";
import CallBellFilledIcon from "@redotech/redo-icons/call-bell_filled.svg";
import ClockFastForwardIcon from "@redotech/redo-icons/clock-fast-forward.svg";
import ClockFastForwardFilledIcon from "@redotech/redo-icons/clock-fast-forward_filled.svg";
import ListIcon from "@redotech/redo-icons/list.svg";
import ListFilledIcon from "@redotech/redo-icons/list_filled.svg";
import SearchIcon from "@redotech/redo-icons/search-md.svg";
import SearchFilledIcon from "@redotech/redo-icons/search-md_filled.svg";
import ShieldTickIcon from "@redotech/redo-icons/shield-tick.svg";
import ShieldTickFilledIcon from "@redotech/redo-icons/shield-tick_filled.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import TagFilledIcon from "@redotech/redo-icons/tag-01_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { useMatch } from "react-router-dom";
import {
  OrderManagementSuiteStatusesContext,
  WAREHOUSE_ICON,
  WAREHOUSE_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { ReplenishmentReportItem } from "./replenishment-report";
import { WorkOrdersItem } from "./work-orders";

export const WarehouseItem = memo(function WarehouseItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { wmsEnabled } = useContext(OrderManagementSuiteStatusesContext);
  const canViewSupport = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER,
  );
  const outboundLabelsRpcClient = useContext(
    RedoOutboundLabelsRpcClientContext,
  );
  // Non-WMS teams with lot-tracked products still get the Lots pages —
  // the recall-lookup surface for picker-entered lots (OMS-3723).
  const lotsOnlyLoad = useLoad(async () => {
    if (wmsEnabled || !outboundLabelsRpcClient) {
      return false;
    }
    return await outboundLabelsRpcClient.hasLotTrackedProducts(null);
  }, [wmsEnabled, outboundLabelsRpcClient]);

  const wmsBaseUrl = `${routes.shippingBaseUrl}/wms`;
  const warehouseMatch = useMatch(`${routes.warehouseBaseUrl}/*`);
  const wmsMatch = useMatch(`${wmsBaseUrl}/*`);
  const anyChildSelected = !!warehouseMatch || !!wmsMatch;

  const is3PL = team.teamType === TeamType.FulfillmentService;
  const showSupport = is3PL && canViewSupport;

  if (!wmsEnabled) {
    if (!lotsOnlyLoad.value) {
      return null;
    }
    return (
      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={anyChildSelected}
        FilledIcon={WAREHOUSE_ICON_FILLED}
        friendlyName="Warehouse management"
        id="warehouse-management"
        UnfilledIcon={WAREHOUSE_ICON}
      >
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${wmsBaseUrl}/lots` }}
          FilledIcon={TagFilledIcon}
          friendlyName="Lots"
          id="lots"
          UnfilledIcon={TagIcon}
        />
      </RedoSecondaryNavItem>
    );
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={anyChildSelected}
      FilledIcon={WAREHOUSE_ICON_FILLED}
      friendlyName="Warehouse"
      id="warehouse-management"
      UnfilledIcon={WAREHOUSE_ICON}
    >
      <WarehouseOverviewItem />
      <BinsItem />
      <InventoryLedgerItem />
      <LotsItem />
      {is3PL && <ProcessingPipelineItem />}
      {is3PL && <QaMetricsItem />}
      <ReplenishmentReportItem />
      <WarehouseServiceRequestsItem />
      {showSupport && <WarehouseSupportItem />}
      {is3PL && <WarehouseSlasItem />}
      <WorkOrdersItem />
    </RedoSecondaryNavItem>
  );
});

const WarehouseOverviewItem = memo(function WarehouseOverviewItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.warehouseHref }}
      FilledIcon={WAREHOUSE_ICON_FILLED}
      friendlyName="Dashboard"
      id="warehouse-overview"
      UnfilledIcon={WAREHOUSE_ICON}
    />
  );
});

const BinsItem = memo(function BinsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.shippingBaseUrl}/wms/storage-locations`,
      }}
      FilledIcon={BoxFilledIcon}
      friendlyName="Bins"
      id="bins"
      UnfilledIcon={BoxIcon}
    />
  );
});

const InventoryLedgerItem = memo(function InventoryLedgerItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.shippingBaseUrl}/wms/inventory-level-ledger`,
      }}
      FilledIcon={ListFilledIcon}
      friendlyName="Inventory ledger"
      id="inventory-ledger"
      UnfilledIcon={ListIcon}
    />
  );
});

const LotsItem = memo(function LotsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: `${routes.shippingBaseUrl}/wms/lots` }}
      FilledIcon={TagFilledIcon}
      friendlyName="Lots"
      id="lots"
      UnfilledIcon={TagIcon}
    />
  );
});

const ProcessingPipelineItem = memo(function ProcessingPipelineItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.processingPipelineHref }}
      FilledIcon={ClockFastForwardFilledIcon}
      friendlyName="Processing pipeline"
      id="processing-pipeline"
      UnfilledIcon={ClockFastForwardIcon}
    />
  );
});

const QaMetricsItem = memo(function QaMetricsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.qaMetricsHref }}
      FilledIcon={ShieldTickFilledIcon}
      friendlyName="QA metrics"
      id="qa-metrics"
      UnfilledIcon={ShieldTickIcon}
    />
  );
});

const WarehouseServiceRequestsItem = memo(
  function WarehouseServiceRequestsItem() {
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.shippingBaseUrl}/wms/service-requests`,
        }}
        FilledIcon={CallBellFilledIcon}
        friendlyName="Service Requests"
        id="service-requests"
        UnfilledIcon={CallBellIcon}
      />
    );
  },
);

const WarehouseSlasItem = memo(function WarehouseSlasItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.warehouseSlasHref }}
      FilledIcon={BarChart01FilledIcon}
      friendlyName="Warehouse SLAs"
      id="warehouse-slas"
      UnfilledIcon={BarChart01Icon}
    />
  );
});

const WarehouseSupportItem = memo(function WarehouseSupportItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: `${routes.shippingBaseUrl}/wms/support` }}
      FilledIcon={SearchFilledIcon}
      friendlyName="Support"
      id="support"
      UnfilledIcon={SearchIcon}
    />
  );
});
